import { z } from 'zod'

import {
  agentPhoneRequestSchema,
  agentSchema,
  callingMeSchema,
  callPageSchema,
  callSchema,
  connectionTestSchema,
  deviceLogResponseSchema,
  dispositionRequestSchema,
  dispositionSchema,
  providerConfigRequestSchema,
  providerConfigSchema,
  setDispositionRequestSchema,
  startCallRequestSchema,
  webhookTokenSchema,
  androidAppInfoSchema,
  type Agent,
  type AgentPhoneRequest,
  type AndroidAppInfo,
  type Call,
  type CallingMe,
  type CallLogFilters,
  type CallPage,
  type ConnectionTest,
  type DeviceLogEntry,
  type Disposition,
  type DispositionRequest,
  type ProviderConfig,
  type ProviderConfigRequest,
  type SetDispositionRequest,
  type StartCallRequest,
  type WebhookToken,
} from '@/types/api/calling'

import { apiClient } from './client'

// ─── Calls ────────────────────────────────────────────────────────────────────

/** 409 (another call in progress) is reported by the caller, not the global toast. */
export async function startCall(data: StartCallRequest): Promise<Call> {
  const response = await apiClient.post('/calls', startCallRequestSchema.parse(data), { suppressErrorToast: true })
  return callSchema.parse(response.data)
}

export async function getCall(callId: string): Promise<Call> {
  const response = await apiClient.get(`/calls/${callId}`)
  return callSchema.parse(response.data)
}

export async function getRecordCalls(recordId: string, page = 0, size = 20): Promise<CallPage> {
  const response = await apiClient.get('/calls', { params: { recordId, page, size } })
  return callPageSchema.parse(response.data)
}

export async function getCallLog(filters: CallLogFilters, page = 0, size = 25): Promise<CallPage> {
  const response = await apiClient.get('/calls', { params: { ...filters, page, size } })
  return callPageSchema.parse(response.data)
}

export async function setCallDisposition(callId: string, data: SetDispositionRequest): Promise<Call> {
  const response = await apiClient.put(`/calls/${callId}/disposition`, setDispositionRequestSchema.parse(data))
  return callSchema.parse(response.data)
}

export async function linkCallToRecord(callId: string, recordId: string): Promise<Call> {
  const response = await apiClient.post(`/calls/${callId}/link`, { recordId })
  return callSchema.parse(response.data)
}

// ─── SIM calls (Android app) ──────────────────────────────────────────────────

export async function reportDeviceDialing(callId: string, deviceId: string): Promise<void> {
  await apiClient.post(`/calls/${callId}/device-event`, {
    type: 'DIALING',
    deviceId,
    occurredAt: new Date().toISOString(),
  })
}

export async function reportDeviceResult(
  callId: string,
  data: { deviceId: string; callLogId: string; durationSec: number; startedAt: string; endedAt?: string; simAccountId?: string }
): Promise<void> {
  await apiClient.post(`/calls/${callId}/device-result`, data)
}

export async function uploadDeviceLog(deviceId: string, entries: DeviceLogEntry[]) {
  const response = await apiClient.post('/calls/device-log', { deviceId, entries })
  return deviceLogResponseSchema.parse(response.data)
}

// ─── Me, agents, devices ──────────────────────────────────────────────────────

export async function getCallingMe(): Promise<CallingMe> {
  const response = await apiClient.get('/calling/me')
  return callingMeSchema.parse(response.data)
}

export async function updateMyPhone(data: AgentPhoneRequest): Promise<CallingMe> {
  const response = await apiClient.put('/calling/me/phone', agentPhoneRequestSchema.parse(data))
  return callingMeSchema.parse(response.data)
}

export async function startPhoneVerification(): Promise<CallingMe> {
  const response = await apiClient.post('/calling/me/phone/verify')
  return callingMeSchema.parse(response.data)
}

export async function confirmPhoneVerification(): Promise<CallingMe> {
  const response = await apiClient.post('/calling/me/phone/verify/confirm')
  return callingMeSchema.parse(response.data)
}

export async function getAgents(): Promise<Agent[]> {
  const response = await apiClient.get('/calling/agents')
  return z.array(agentSchema).parse(response.data)
}

export async function setAgentPhone(userId: string, data: AgentPhoneRequest): Promise<Agent> {
  const response = await apiClient.put(`/calling/agents/${userId}/phone`, agentPhoneRequestSchema.parse(data))
  return agentSchema.parse(response.data)
}

export async function verifyAgent(userId: string): Promise<Agent> {
  const response = await apiClient.post(`/calling/agents/${userId}/verify`)
  return agentSchema.parse(response.data)
}

export async function getAppVersion(): Promise<AndroidAppInfo> {
  const response = await apiClient.get('/calling/app-version')
  return androidAppInfoSchema.parse(response.data)
}

export { registerDevice, removeDevice } from './device.service'

// ─── Telephony settings ───────────────────────────────────────────────────────

export async function getProviderConfig(): Promise<ProviderConfig> {
  const response = await apiClient.get('/calling/config')
  return providerConfigSchema.parse(response.data)
}

export async function saveProviderConfig(data: ProviderConfigRequest): Promise<ProviderConfig> {
  const response = await apiClient.put('/calling/config', providerConfigRequestSchema.parse(data))
  return providerConfigSchema.parse(response.data)
}

export async function testProviderConnection(): Promise<ConnectionTest> {
  const response = await apiClient.post('/calling/config/test')
  return connectionTestSchema.parse(response.data)
}

export async function rotateWebhookToken(): Promise<WebhookToken> {
  const response = await apiClient.post('/calling/config/rotate-webhook-token')
  return webhookTokenSchema.parse(response.data)
}

export async function getDispositions(includeInactive = false): Promise<Disposition[]> {
  const response = await apiClient.get('/calling/dispositions', { params: { includeInactive } })
  return z.array(dispositionSchema).parse(response.data)
}

export async function createDisposition(data: DispositionRequest): Promise<Disposition> {
  const response = await apiClient.post('/calling/dispositions', dispositionRequestSchema.parse(data))
  return dispositionSchema.parse(response.data)
}

export async function updateDisposition(id: string, data: DispositionRequest): Promise<Disposition> {
  const response = await apiClient.put(`/calling/dispositions/${id}`, dispositionRequestSchema.parse(data))
  return dispositionSchema.parse(response.data)
}

export async function deactivateDisposition(id: string): Promise<void> {
  await apiClient.delete(`/calling/dispositions/${id}`)
}

export async function reorderDispositions(orderedIds: string[]): Promise<Disposition[]> {
  const response = await apiClient.put('/calling/dispositions/order', orderedIds)
  return z.array(dispositionSchema).parse(response.data)
}
